import React from "react";
import { OutreachEmailView } from "./views/OutreachEmailView";
import { PersonOverview } from "./crm/PersonOverview";
import { CompanyOverview } from "./crm/CompanyOverview";
import { EntityEmailPanel } from "./crm/EntityEmailPanel";
import { MailboxView } from "./crm/MailboxView";
import { emailActions } from "./actions/emailActions";
import { UrlField, UrlPreview } from "./fields/UrlField";

/**
 * What each collection gets in the admin on top of its config file — a module
 * of its own so it can be tested without the app shell, which needs a login.
 */

/**
 * The review actions are attached to the emails collection itself.
 *
 * Passing them to `<RebaseCMS entityActions={…}>` only *registers* them, so a
 * collection can name one by key — and the keys reached the browser and still
 * did not resolve onto the entity form. Attaching the objects to the collection
 * is the form that actually renders, and it does not depend on a lookup.
 */
/**
 * Every string property that declared itself a URL gets a link.
 *
 * Read off the property, never a list of field names: a column added tomorrow
 * is covered the moment it says `url: true` (or is named as a domain), and a
 * list would have to be remembered. Nine properties across five collections
 * match today.
 *
 * The admin at 0.19.1 renders a link only for `admin.urlPreview`, so a property
 * that correctly declared itself a URL still came out as text you had to select
 * and copy — see `fields/UrlField.tsx`. `Field` is the editing control and
 * `Preview` is everything else (table cell, card line, chip); overriding one and
 * not the other is how you get a link in the form and plain text in the list.
 */
export function withUrlLinks<T extends { properties?: Record<string, unknown> }>(collection: T): T {
    const properties = collection.properties;
    if (!properties) return collection;

    let touched = false;
    const next: Record<string, unknown> = {};
    for (const [key, property] of Object.entries(properties)) {
        const p = property as { type?: string; url?: boolean; admin?: Record<string, unknown> };
        // Two ways a string column is a web address, and they are different
        // claims:
        //
        //   · `url: true` — it holds a URI. The OpenAPI contract's
        //     `format: uri` is generated from this, so it is a statement about
        //     the data and not about the screen.
        //   · a `domain` column — it holds a bare HOST. `junip.co` is something
        //     you want to click and is not a URI, so declaring `url: true` on it
        //     to get a link would put a lie in the contract.
        //
        // Matched on the column name for the second, which is the one rule here
        // that is a convention rather than a declaration — and it is bounded to
        // exactly `domain` and `*_domain`, because the field reads the property
        // to decide whether a bare host may become a link at all.
        const holdsAddress = p?.type === "string"
            && (p.url === true || /^domain$|_domain$/.test(key));
        if (!holdsAddress) { next[key] = property; continue; }
        touched = true;
        next[key] = {
            ...p,
            admin: {
                ...p.admin,
                // Anything the collection already said wins: a property that
                // asks for an image preview keeps it.
                Field: p.admin?.Field ?? UrlField,
                Preview: p.admin?.Preview ?? UrlPreview
            }
        };
    }
    return touched ? { ...collection, properties: next } : collection;
}

/**
 * An email opens as an email: `formView` swaps the field-by-field record form
 * for a composer with Send in it. Attached here rather than in
 * `config/collections/emails.ts` for the same reason as the actions — the
 * backend loads that file for its schema and must not pull React in with it.
 */
const emailFormView = { Builder: OutreachEmailView };

type EntityViewProps = { entity?: { id?: string | number; values?: Record<string, unknown> } };
const text = (value: unknown) => (typeof value === "string" ? value : "");

/**
 * A person or a company opens on its cover — who they are, the conversation,
 * what to do next — with the field form one tab to the right. A NEW record
 * opens on the form, because there is nothing to cover yet.
 */
const overviewFirst = ({ status }: { status?: string }) => (status === "new" ? undefined : "overview");

const personViews = [
    { key: "overview", name: "Overview", position: "start" as const, Builder: PersonOverview },
    {
        key: "email",
        name: "Email",
        Builder: ({ entity }: EntityViewProps) => (
            <div className="p-6 max-w-4xl">
                <EntityEmailPanel
                    kind="person"
                    entityId={String(entity?.id ?? "")}
                    email={text(entity?.values?.email) || null}
                    name={text(entity?.values?.display_name) || text(entity?.values?.handle)}
                    companyName={text(entity?.values?.company)}
                />
            </div>
        )
    }
];

const companyViews = [
    { key: "overview", name: "Overview", position: "start" as const, Builder: CompanyOverview },
    {
        key: "email",
        name: "Email",
        Builder: ({ entity }: EntityViewProps) => (
            <div className="p-6 max-w-4xl">
                <EntityEmailPanel kind="company" entityId={String(entity?.id ?? "")} name={text(entity?.values?.name)} />
            </div>
        )
    }
];

/**
 * What each collection gets on top of its config file, which the backend also
 * loads and so must stay free of React.
 *
 * The raw conversation and message tables are hidden from the sidebar — the
 * inbox is how mail is read, and a table of messages opened by accident is a
 * database, not an email. They stay reachable by URL for anybody debugging.
 */
export function adminCollections<T extends { slug: string; properties?: Record<string, unknown> }>(collections: readonly T[]): T[] {
    return collections.map(withUrlLinks).map(collection => {
    const admin = (collection as { admin?: Record<string, unknown> }).admin ?? {};
    switch (collection.slug) {
        case "emails":
            return { ...collection, entityActions: emailActions, admin: { ...admin, entityActions: emailActions, formView: emailFormView } };
        case "prospects":
            return { ...collection, admin: { ...admin, entityViews: personViews, defaultSelectedView: overviewFirst } };
        case "companies":
            return { ...collection, admin: { ...admin, entityViews: companyViews, defaultSelectedView: overviewFirst } };
        case "mailboxes":
            return { ...collection, admin: { ...admin, formView: { Builder: MailboxView } } };
        case "email_threads":
        case "email_messages":
            return { ...collection, admin: { ...admin, hideFromNavigation: true } };
        default:
            return collection;
    }
    }) as T[];
}
